import {FC, useState} from 'react';
import block from 'bem-cn-lite';
import {Button, Icon, Loader, Text, Tooltip} from '@gravity-ui/uikit';
import {ArrowRotateRight, MagicWand} from '@gravity-ui/icons';
import {KeyboardCloseButton} from '@components/KeyboardNavigation';
import {CApi as userGwApi} from '@services/api';

import './PopupGenerationPassword.scss';

const b = block('popup-generation-password');

interface PopupGenerationPasswordProps {
    closePopover?: () => void;
    onApplyPassword?: (password: string) => void;
}

export const PopupGenerationPassword: FC<PopupGenerationPasswordProps> = ({
    closePopover,
    onApplyPassword
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [password, setPassword] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);
    const [openTooltipPassword, setOpenTooltipPassword] = useState(false);

    const title = password ? 'Пароль сгенерирован' : 'Генерация пароля';

    const handleGeneratePassword = () => {
        setIsLoading(true);
        setHasError(false);

        userGwApi()
            .generatePassword.generatePasswordList()
            .then((res) => {
                setHasError(false);
                setPassword(res.data.password);
            })
            .catch(() => {
                setHasError(true);
                setPassword(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleApply = () => {
        if (password) {
            onApplyPassword?.(password);
        }
        closePopover?.();
    };

    const handleCancel = () => {
        closePopover?.();
    };

    return (
        <div className={b()}>
            <div className={b('header')}>
                <Text variant="subheader-3">{title}</Text>
                <KeyboardCloseButton onClose={closePopover} className={b('close-icon')} />
            </div>

            {isLoading ? (
                <Loader size="s" />
            ) : (
                <>
                    {/* Состояние после генерации / ошибки */}
                    {password || hasError ? (
                        <>
                            <div className={b('generated-wrapper')}>
                                {hasError ? (
                                    <Text variant="body-1" color="danger">
                                        Ошибка генерации
                                    </Text>
                                ) : (
                                    <Tooltip
                                        content={
                                            <Text variant="body-1" className={b('tooltip-content')}>
                                                Скопировано
                                            </Text>
                                        }
                                        placement="top"
                                        disabled={!openTooltipPassword}
                                        openDelay={0}
                                        contentClassName={b('tooltip')}
                                        className={b('tooltip-container')}
                                    >
                                        <div
                                            className={b('copy-password')}
                                            onClick={() => {
                                                setOpenTooltipPassword(true);
                                                navigator.clipboard.writeText(password || '');
                                                setTimeout(
                                                    () => setOpenTooltipPassword(false),
                                                    1000
                                                );
                                            }}
                                        >
                                            <Text variant="body-1">{password}</Text>
                                        </div>
                                    </Tooltip>
                                )}

                                <Tooltip
                                    content={
                                        <Text variant="body-1" className={b('tooltip-content')}>
                                            Сгенерировать ещё
                                        </Text>
                                    }
                                    placement="top"
                                    disabled={false}
                                    openDelay={0}
                                    closeDelay={0}
                                    contentClassName={b('tooltip')}
                                    className={b('tooltip-container')}
                                >
                                    <Button size="xs" onClick={handleGeneratePassword}>
                                        <Icon size={12} data={ArrowRotateRight} />
                                    </Button>
                                </Tooltip>
                            </div>

                            <div className={b('footer-buttons')}>
                                <Button
                                    view="action"
                                    size="m"
                                    className={b('button-apply')}
                                    onClick={handleApply}
                                    disabled={hasError}
                                >
                                    Применить
                                </Button>
                                <Button
                                    view="flat"
                                    size="m"
                                    className={b('button-cancel')}
                                    onClick={handleCancel}
                                >
                                    Отмена
                                </Button>
                            </div>
                        </>
                    ) : (
                        // Начальное состояние
                        <>
                            <Text variant="body-1" color="complementary" className={b('text')}>
                                Поможем сгенерировать новый пароль. Не забудьте его сохранить
                            </Text>

                            <Button
                                view="action"
                                size="m"
                                className={b('button')}
                                onClick={handleGeneratePassword}
                            >
                                <Icon size={16} data={MagicWand} />
                                <Text variant="body-1">Сгенерировать</Text>
                            </Button>
                        </>
                    )}
                </>
            )}
        </div>
    );
};
